import { useEffect, useRef, useState } from 'react';

import type { TypeScrollRevealProps } from './types';

const EFFECTS = [
  'Fade',
  'FadeUp',
  'FadeDown',
  'FadeLeft',
  'FadeRight',
  'ZoomIn',
];

// Mismo rango que el custom field `Velocidad` en Strapi (range-input 100–3000).
// Clamp defensivo por si llega un valor fuera de rango de una API vieja.
const MIN_DURATION = 100;
const MAX_DURATION = 3000;

type TypeState = 'hidden' | 'shown' | 'done';

/**
 * ScrollReveal
 *
 * Animación de entrada de un bloque de página: el contenido arranca oculto y
 * se revela —una sola vez— cuando el bloque entra en el viewport. Lo alimenta
 * el componente Strapi `revisadas.animacion` (`Efecto` + `Velocidad`) que
 * `DynamicComponents` pasa aquí; los keyframes viven en `styles/globals.css`.
 *
 * Sin `Efecto` no envolvemos nada y devolvemos los hijos tal cual, así los
 * bloques que nadie ha configurado renderizan exactamente el mismo árbol DOM
 * que antes.
 *
 * Tres estados, y el tercero importa:
 *
 * - `hidden`: estado que se serializa en el HTML de SSR (opacity 0). Sin esto
 *   habría flash de contenido visible antes de hidratar.
 * - `shown`: corre el keyframe.
 * - `done`: al terminar quitamos TODOS los atributos. Un elemento con
 *   `transform`/`opacity` propios crea containing block y rompe los hijos
 *   `position: fixed` (controles de Leaflet en PropertyMap, modales…), así que
 *   en cuanto acaba la animación el wrapper vuelve a ser un div pelado.
 *
 * `prefers-reduced-motion` salta directamente a `done`, y `_document.tsx` lleva
 * un `<noscript>` que fuerza la visibilidad si el JS nunca llega a ejecutarse.
 */
export const ScrollReveal = ({
  animacion,
  children,
}: TypeScrollRevealProps) => {
  const effect = animacion?.Efecto;
  const enabled = !!effect && EFFECTS.includes(effect);
  const isHorizontal = effect === 'FadeLeft' || effect === 'FadeRight';

  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<TypeState>('hidden');

  useEffect(() => {
    if (!enabled) return;

    const el = ref.current;
    if (!el) return;

    // Sin IntersectionObserver (o con movimiento reducido) no animamos: el
    // bloque tiene que verse igualmente.
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      setState('done');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      setState('shown');
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [enabled]);

  if (!enabled) return <>{children}</>;

  // `Velocidad` = duración en ms. Sin valor no seteamos la var y manda el
  // default de globals.css (1500ms).
  const speed = animacion?.Velocidad;
  const duration =
    typeof speed === 'number' && Number.isFinite(speed)
      ? Math.min(Math.max(Math.round(speed), MIN_DURATION), MAX_DURATION)
      : null;

  const animated = (
    <div
      ref={ref}
      {...(state !== 'done' && {
        'data-reveal': effect,
        'data-reveal-state': state,
        ...(duration && {
          style: {
            '--reveal-duration': `${duration}ms`,
          } as React.CSSProperties,
        }),
      })}
      onAnimationEnd={(event) => {
        // Los eventos de animación burbujean: sólo nos interesa la nuestra.
        if (event.target === event.currentTarget) setState('done');
      }}
    >
      {children}
    </div>
  );

  // `FadeLeft`/`FadeRight` desplazan el bloque 32px en horizontal. En un bloque
  // a todo el ancho, el desplazamiento a la derecha SACA la caja del documento:
  // pasa a medir 425px contra un viewport de 393 y Safari iOS reescala la
  // página entera al ~92% mientras dura la animación, devolviéndola al acabar
  // (se percibía como que "la página se redimensiona sola" al scrollear).
  //
  // El clip tiene que vivir en un padre que NO se mueve: sobre la propia caja
  // desplazada sólo recorta a los hijos, no a sí misma, y el documento se
  // ensancha igual. Envolver en un elemento con `overflow-x: clip` sí saca el
  // desbordamiento del cálculo de anchura del documento.
  //
  // El wrapper es permanente a propósito: depende sólo de `Efecto`, que no
  // cambia en runtime. Montarlo y desmontarlo al pasar a `done` cambiaría la
  // profundidad del árbol y React remontaría el bloque entero (imágenes
  // recargando, mapas reinicializándose).
  return isHorizontal ? <div data-reveal-clip>{animated}</div> : animated;
};

ScrollReveal.displayName = 'ScrollReveal';
